import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * useAutosave — Manages autosave (debounced + interval + before-unload),
 * inactivity draft save, restore dialog, guest session, team auto-share.
 */
export const useAutosave = ({
  buildData, restoreData, effectiveRole, guestId, user,
  getNotes, getResult, isGenerating, loadHistory, patientLabel,
}) => {
  const [lastAutoSave, setLastAutoSave] = useState(null);
  // Perf fix (Mar 3, 2026) — ref instead of state so keydown/mousedown/
  // touchstart activity events don't force a full-tree re-render on every
  // keystroke (that was the "typing lag" the provider reported).
  const lastActivityTimeRef = useRef(Date.now());
  const [autoSavedDraft, setAutoSavedDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [pendingRestoreData, setPendingRestoreData] = useState(null);
  const [autoShareTeamId, setAutoShareTeamId] = useState(() => localStorage.getItem('writeright_auto_share_team') || null);
  const [autoShareDrafts, setAutoShareDrafts] = useState(() => localStorage.getItem('writeright_auto_share_drafts') === 'true');
  const AUTO_SAVE_TIMEOUT = 3 * 60 * 1000;

  // Auto-enable sharing when user has an active team — no more manual toggle each day
  useEffect(() => {
    if (!autoShareTeamId) {
      const activeTeam = localStorage.getItem('writeright_active_team');
      if (activeTeam) {
        setAutoShareTeamId(activeTeam);
        localStorage.setItem('writeright_auto_share_team', activeTeam);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save on changes (5s)
  useEffect(() => {
    const notes = getNotes();
    if (!notes || notes.trim().length < 10) return;
    const t = setTimeout(() => { localStorage.setItem('writeright_autosave', JSON.stringify(buildData())); setLastAutoSave(new Date()); }, 5000);
    return () => clearTimeout(t);
  }, [buildData, getNotes]);

  // Interval save (10s)
  useEffect(() => {
    const i = setInterval(() => {
      const notes = getNotes();
      if (notes && notes.trim().length >= 10) { localStorage.setItem('writeright_autosave', JSON.stringify(buildData())); setLastAutoSave(new Date()); }
    }, 10000);
    return () => clearInterval(i);
  }, [buildData, getNotes]);

  // Warn before unload
  useEffect(() => {
    const handler = (e) => {
      const notes = getNotes();
      const result = getResult();
      if ((notes && notes.trim().length >= 10) || result) {
        localStorage.setItem('writeright_autosave', JSON.stringify(buildData()));
        e.preventDefault(); e.returnValue = ''; return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [buildData, getNotes, getResult]);

  // Activity tracking
  useEffect(() => {
    const h = () => { lastActivityTimeRef.current = Date.now(); setAutoSavedDraft(false); };
    window.addEventListener('keydown', h); window.addEventListener('mousedown', h); window.addEventListener('touchstart', h);
    return () => { window.removeEventListener('keydown', h); window.removeEventListener('mousedown', h); window.removeEventListener('touchstart', h); };
  }, []);

  // Auto-save draft on 3min inactivity
  useEffect(() => {
    const notes = getNotes();
    const result = getResult();
    if (effectiveRole !== 'provider' || !notes || notes.trim().length < 50 || result || autoSavedDraft || isGenerating) return;
    const check = setInterval(() => {
      const currentNotes = getNotes();
      if (Date.now() - lastActivityTimeRef.current >= AUTO_SAVE_TIMEOUT && !autoSavedDraft && !isGenerating && !getResult()) {
        setAutoSavedDraft(true);
        (async () => {
          try {
            const data = buildData();
            const ep = currentDraftId ? `${API}/history/update-draft` : `${API}/history/save-draft`;
            const res = await axios.post(ep, {
              notes: currentNotes,
              patient_age: data.patientAge ? parseInt(data.patientAge) : null,
              patient_gender: data.patientGender || null,
              patient_status: data.patientStatus || null,
              chief_complaint: data.chiefComplaint || null,
              imaging_orders: (data.imagingOrders || []).filter(i => i.type?.trim()) || null,
              specialties: (data.specialties || []).filter(s => s?.trim()) || null,
              icd10_codes: data.manualIcd10 || null,
              guest_id: guestId,
              ...(currentDraftId && { draft_id: currentDraftId })
            }, { withCredentials: true });
            if (res.data.success) {
              if (res.data.draft_id && !currentDraftId) setCurrentDraftId(res.data.draft_id);
              toast.success(currentDraftId ? 'Draft updated!' : 'Draft auto-saved!', { duration: 3000 });
              loadHistory();
              if (autoShareDrafts && autoShareTeamId) {
                try { await axios.post(`${API}/teams/${autoShareTeamId}/share-draft`, { draft_id: res.data.draft_id || currentDraftId, notes: currentNotes, patient_label: patientLabel }, { withCredentials: true }); } catch (e) { console.warn('Auto-share draft failed:', e.message); }
              }
            }
          } catch (e) { console.error('Auto-save failed:', e); }
        })();
      }
    }, 30000);
    return () => clearInterval(check);
  }, [effectiveRole, autoSavedDraft, isGenerating, currentDraftId, guestId, autoShareDrafts, autoShareTeamId, patientLabel, getNotes, getResult, buildData, loadHistory, AUTO_SAVE_TIMEOUT]);

  // Restore dialog on mount
  useEffect(() => {
    const saved = localStorage.getItem('writeright_autosave');
    if (saved) {
      try { const d = JSON.parse(saved); if (d.notes?.trim().length >= 10) { setPendingRestoreData(d); setShowRestoreDialog(true); } else localStorage.removeItem('writeright_autosave'); }
      catch (e) { localStorage.removeItem('writeright_autosave'); }
    }
  }, []);

  const handleRestoreChoice = useCallback((choice, clearAll, setShowHistory) => {
    if (choice === 'restore' && pendingRestoreData) { restoreData(pendingRestoreData); toast.success('Session restored!', { duration: 2000 }); }
    else if (choice === 'done') { localStorage.removeItem('writeright_autosave'); clearAll(true); toast.success('Cleared for new patient', { duration: 2000 }); }
    else if (choice === 'history') { localStorage.removeItem('writeright_autosave'); setShowHistory(true); setTimeout(() => { document.querySelector('[data-testid="history-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); toast.success('Opening your history...', { duration: 2000 }); }
    else if (choice === 'discard') { localStorage.removeItem('writeright_autosave'); toast.info('Saved data discarded', { duration: 1500 }); }
    setShowRestoreDialog(false); setPendingRestoreData(null);
  }, [pendingRestoreData, restoreData]);

  const saveGuestSession = useCallback(() => { localStorage.setItem('writeright_guest_session', JSON.stringify(buildData())); }, [buildData]);
  const restoreGuestSession = useCallback(() => { const s = localStorage.getItem('writeright_guest_session'); if (s) { try { restoreData(JSON.parse(s)); localStorage.removeItem('writeright_guest_session'); toast.success('Your session has been restored!'); } catch (e) { console.warn('Guest session restore failed:', e.message); } } }, [restoreData]);

  // Restore after OAuth
  useEffect(() => {
    const shouldRestore = localStorage.getItem('writeright_pending_restore');
    if (shouldRestore && user) {
      localStorage.removeItem('writeright_pending_restore');
      const gs = localStorage.getItem('writeright_guest_session');
      if (gs) restoreGuestSession();
      else { const a = localStorage.getItem('writeright_autosave'); if (a) { try { const d = JSON.parse(a); if (d.notes?.trim().length > 10) { restoreData(d); toast.success('Your work has been restored from autosave!'); } } catch (e) { console.warn('Autosave restore failed:', e.message); } } }
    }
  }, [user, restoreGuestSession, restoreData]);

  // Team sharing toggles
  const toggleAutoShareTeam = useCallback((teamId) => {
    if (autoShareTeamId === teamId) { setAutoShareTeamId(null); localStorage.removeItem('writeright_auto_share_team'); toast.info('Auto-share disabled'); }
    else { setAutoShareTeamId(teamId); localStorage.setItem('writeright_auto_share_team', teamId); toast.success('Auto-share enabled for this team'); }
  }, [autoShareTeamId]);

  const toggleAutoShareDrafts = useCallback(() => {
    const next = !autoShareDrafts;
    setAutoShareDrafts(next); localStorage.setItem('writeright_auto_share_drafts', String(next));
    toast.info(next ? 'Draft streaming to team enabled' : 'Draft streaming disabled');
  }, [autoShareDrafts]);

  const quickShareToTeam = useCallback(async (noteId) => {
    const teamId = autoShareTeamId || localStorage.getItem('writeright_active_team');
    if (!teamId) { toast.error('No active team — open Team Board first'); return; }
    try { await axios.post(`${API}/teams/${teamId}/share-note`, { note_id: noteId }, { withCredentials: true }); toast.success('Shared to team'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Share failed'); }
  }, [autoShareTeamId]);

  const shareDraftToTeam = useCallback(async (draftData) => {
    if (!autoShareTeamId || !autoShareDrafts) return;
    try { await axios.post(`${API}/teams/${autoShareTeamId}/share-draft`, draftData, { withCredentials: true }); }
    catch (err) { console.error('Draft share failed:', err); }
  }, [autoShareTeamId, autoShareDrafts]);

  return {
    lastAutoSave, currentDraftId, setCurrentDraftId,
    showRestoreDialog, pendingRestoreData, handleRestoreChoice,
    autoShareTeamId, autoShareDrafts,
    toggleAutoShareTeam, toggleAutoShareDrafts,
    quickShareToTeam, shareDraftToTeam,
    saveGuestSession, restoreGuestSession,
    autoSavedDraft, setAutoSavedDraft,
  };
};

export default useAutosave;
